<script setup lang="ts">
import { defineProps } from "vue";
import { BaziResponse } from "@/types/calculators";
import NatalBirthdayDecktop from "@/views/Calculators/BaZiPrediction/components/NatalBirthdayDecktop.vue";
import NatalHiddenDesktop from "@/views/Calculators/BaZiPrediction/components/NatalHiddenDesktop.vue";
import NatalPalaces from "@/views/Calculators/BaZiPrediction/components/NatalPalaces.vue";
import ElementsBalance from "@/views/Calculators/BaZiPrediction/components/ElementsBalance.vue";
import DecadesOfLuck from "@/views/Calculators/BaZiPrediction/components/DecadesOfLuck.vue";
import AnnualsLuck from "@/views/Calculators/BaZiPrediction/components/AnnualsLuck.vue";
import NatalBirthdayMobile from "@/views/Calculators/BaZiPrediction/components/NatalBirthdayMobile.vue";
import NatalHiddenMobile from "@/views/Calculators/BaZiPrediction/components/NatalHiddenMobile.vue";
import NatalPalacesPillarTable from "@/views/Calculators/BaZiPrediction/components/NatalPalacesPillarTable.vue";
import { useI18n } from "vue-i18n";
import PersonalIndicators from "@/views/Calculators/BaZiPrediction/components/PersonalIndicators.vue";

const props = defineProps<{ data: BaziResponse; inputYear: number }>();

const { t } = useI18n();
</script>

<template>
  <div class="mb-6 mx-auto max-w-[812px] flex flex-col gap-6">
    <!-- PERSONAL INDICATORS -->
    <div class="md:bg-white rounded-[20px] text-center">
      <div
        class="rounded-t-[20px] max-md:rounded-b-[20px] h-14 flex justify-center items-center bg-brown"
      >
        <span class="text-white text-2xl font-semibold">
          {{ t("calculators.bazi.results.personal-indicators.title") }}
        </span>
      </div>
      <PersonalIndicators
        class="pb-6 md:px-10 pt-10"
        :personal-indicators="data.personal_indicators"
      />
    </div>
    <!-- Birthday map -->
    <div class="md:bg-white rounded-[20px] text-center">
      <div
        class="rounded-t-[20px] max-md:rounded-b-[20px] h-14 flex justify-center items-center bg-brown"
      >
        <span class="text-white text-2xl font-semibold">
          {{ t("calculators.bazi.results.birthday-map.title") }}
        </span>
      </div>
      <div class="pb-6 md:px-10 pt-10">
        <h4
          class="text-xl max-md:leading-[20px] max-md:text-left md:text-2xl font-semibold mb-4"
        >
          {{ t("calculators.bazi.results.birthday-map.four-stems") }}
        </h4>
        <NatalBirthdayDecktop
          :pillars="props.data.natal.pillars"
          class="max-md:hidden"
        />
        <NatalBirthdayMobile
          :pillars="props.data.natal.pillars"
          class="md:hidden"
        />

        <h4
          class="text-xl max-md:leading-[20px] max-md:text-left md:text-2xl font-semibold mb-4 mt-6 md:mt-10"
        >
          {{ t("calculators.bazi.results.birthday-map.hidden-stems") }}
        </h4>
        <NatalHiddenDesktop
          :hidden="props.data.natal.hidden"
          class="max-md:hidden"
        />
        <NatalHiddenMobile
          :hidden="props.data.natal.hidden"
          class="md:hidden"
        />

        <h4
          class="text-xl max-md:leading-[20px] max-md:text-left md:text-2xl font-semibold mb-4 mt-6 md:mt-10"
        >
          {{ t("calculators.bazi.results.birthday-map.12-palaces") }}
        </h4>
        <NatalPalaces
          :palaces="props.data.natal.palaces"
          :pillars="props.data.natal.pillars"
          class="max-md:hidden"
        />
        <NatalPalacesPillarTable
          :palaces="props.data.natal.palaces"
          :pillars="props.data.natal.pillars"
          class="md:hidden"
        />
      </div>
    </div>

    <!-- Динамика стихий -->
    <div class="md:bg-white rounded-lg text-center">
      <div
        class="rounded-t-[20px] max-md:rounded-b-[20px] h-14 flex justify-center items-center bg-brown"
      >
        <span class="text-white text-2xl font-semibold">
          {{ t("calculators.bazi.results.element-balance.title") }}
        </span>
      </div>
      <div class="pb-6 md:px-10 pt-10">
        <p
          class="text-xl max-md:leading-[24px] max-md:text-left md:text-2xl font-semibold mb-4"
        >
          {{
            t("calculators.bazi.results.element-balance.subtitle", {
              dayMasterStatus: props.data.day_master_status,
            })
          }}
        </p>
        <ElementsBalance :data="props.data.element_balance" />
      </div>
    </div>

    <!-- LUCK FLOW -->
    <div class="md:bg-white rounded-lg text-center">
      <div
        class="rounded-t-[20px] max-md:rounded-b-[20px] h-14 flex justify-center items-center bg-brown"
      >
        <span class="text-white text-2xl font-semibold">
          {{ t("calculators.bazi.results.luck-flow.title") }}
        </span>
      </div>
      <div class="pb-6 md:px-10 pt-10">
        <p
          class="text-xl max-md:leading-[24px] max-md:text-left md:text-2xl font-semibold mb-4"
        >
          {{ t("calculators.bazi.results.luck-flow.decade-luck-title") }}
        </p>
        <DecadesOfLuck
          :start-year="props.inputYear"
          :data="props.data.decades_of_luck"
        />

        <p
          class="text-xl max-md:leading-[24px] max-md:text-left md:text-2xl font-semibold mb-4 mt-6 md:mt-10"
        >
          {{ t("calculators.bazi.results.luck-flow.yearly-trend-title") }}
        </p>
        <AnnualsLuck
          :years="props.data.years"
          :palaces="props.data.natal.palaces"
        />
      </div>
    </div>
  </div>
</template>
